<script>
/**
 * @component DoctorBooking
 * @description Micrositio público de turnera para un médico específico.
 * Layout 2 columnas: perfil (izq) + booking sticky (der).
 * Accesible via /booking/:doctorSlug sin autenticación previa.
 * Pasos de reserva en columna derecha: turnos → identificacion → confirmacion → exito
 * @example <DoctorBooking />
 */
import turneraService from "@/services/turnera";
import api from "@/services/api";
import { getPublicFileUrl } from "@/services/config";
import Spinner from "@/components/ui/Spinner";
import {
  format,
  addDays,
  isSameDay,
  parseISO,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
} from "date-fns";
import { es } from "date-fns/locale";

/** @description Cantidad de días a mostrar en la grilla */
const DAYS_TO_SHOW = 3;

export default {
  name: "DoctorBooking",
  components: { Spinner },

  data() {
    return {
      /** @description Paso actual: turnos | identificacion | confirmacion | exito */
      step: "turnos",
      /** @description Datos del médico cargados del perfil público */
      doctor: null,
      /** @description Slots disponibles del médico */
      slots: [],
      /** @description Loading del perfil y slots iniciales */
      isLoading: true,
      /** @description Día base de la grilla */
      gridStartDay: new Date(),
      /** @description Modo del calendario: 'week' | 'month' */
      calendarMode: "week",
      /** @description Mes base para la vista de mes */
      calendarMonth: new Date(),
      /** @description Si el mapa está expandido */
      mapExpanded: false,
      /** @description Slot seleccionado */
      selectedSlot: null,
      /** @description Turno confirmado */
      confirmedAppointment: null,
      /** @description Loading de confirmación */
      isConfirming: false,
      /** @description Sub-paso de identificacion: login | register */
      authTab: "login",
      /** @description Formulario de login */
      loginForm: { username: "", password: "" },
      /** @description Loading de login */
      isLoggingIn: false,
      /** @description Formulario de registro de nuevo paciente */
      registerForm: {
        firstName: "",
        lastName: "",
        email: "",
        governmentId: { type: "dni", number: "" },
      },
      /** @description Loading de registro */
      isRegistering: false,
      /** @description Paciente autenticado */
      loggedPatient: null,
      /** @description Formulario de confirmación */
      confirmForm: { modalidad: "presencial", motivo: "" },
      /** @description Tipo de consulta seleccionado (UI) */
      selectedService: "",
      /** @description Obra social seleccionada (UI) */
      selectedInsurance: "",
      /** @description Si es primera visita (UI) */
      isFirstVisit: true,
      /** @description Bio expandida */
      bioExpanded: false,
    };
  },

  computed: {
    /**
     * @description Slug del médico desde la ruta
     * @returns {string}
     */
    doctorSlug() {
      return this.$route.params.doctorSlug;
    },

    /**
     * @description Usuario del store
     * @returns {Object|null}
     */
    storeUser() {
      return this.$store.state.user?._id ? this.$store.state.user : null;
    },

    /**
     * @description 7 días del week strip (misma semana que gridStartDay)
     * @returns {Array<Date>}
     */
    weekStripDays() {
      return Array.from({ length: 7 }, (_, i) => addDays(this.weekBase, i));
    },

    /**
     * @description Lunes de la semana que contiene gridStartDay
     * @returns {Date}
     */
    weekBase() {
      const d = new Date(this.gridStartDay);
      const day = d.getDay(); // 0=dom
      const diff = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + diff);
      return d;
    },

    /**
     * @description Días a mostrar en la grilla (siempre DAYS_TO_SHOW=3)
     * @returns {Array<Date>}
     */
    gridDays() {
      return Array.from({ length: DAYS_TO_SHOW }, (_, i) =>
        addDays(this.gridStartDay, i)
      );
    },

    /**
     * @description Slots agrupados por día — incluye disponibles y tomados
     * @returns {Object<string, Array>}
     */
    slotsByDay() {
      const grouped = {};
      this.gridDays.forEach((day) => {
        const key = format(day, "yyyy-MM-dd");
        grouped[key] = this.slots
          .filter(
            (s) =>
              isSameDay(parseISO(s.startsAt), day) &&
              s.isVisibleByPatient !== false
          )
          .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
          .slice(0, 6);
      });
      return grouped;
    },

    /** @returns {boolean} Si hay algún turno disponible en la ventana actual */
    hasSlots() {
      return Object.values(this.slotsByDay).some((slots) =>
        slots.some((s) => s.available)
      );
    },

    /** @returns {string} Nombre completo del médico */
    doctorFullName() {
      if (!this.doctor) return "";
      const { firstName = "", lastName = "" } = this.doctor;
      return `Dr. ${lastName}${firstName ? ", " + firstName : ""}`.trim();
    },

    /** @returns {string} Primera especialidad */
    doctorSpecialty() {
      return this.doctor?.doctor?.medicalSpecialties?.[0]?.name || "";
    },

    /** @returns {Array} Todas las especialidades */
    doctorSpecialties() {
      return this.doctor?.doctor?.medicalSpecialties || [];
    },

    /** @returns {Object|null} Primera institución */
    doctorInstitution() {
      return this.doctor?.doctor?.institutions?.[0] || null;
    },

    /** @returns {string} Matrícula formateada */
    doctorLicense() {
      const lic = this.doctor?.doctor?.license;
      if (!lic?.number) return "";
      return `${lic.type?.toUpperCase() || "MN"} ${lic.number}`;
    },

    /** @returns {string} Dirección de la institución */
    institutionAddress() {
      const addr = this.doctorInstitution?.address;
      if (!addr) return "";
      if (typeof addr === "string") return addr;
      return [addr.street, addr.buildingNumber, addr.city]
        .filter(Boolean)
        .join(" ");
    },

    /** @returns {string} Teléfono */
    institutionPhone() {
      return this.doctorInstitution?.phone || "";
    },

    /** @returns {string} Email */
    institutionEmail() {
      return this.doctorInstitution?.email || "";
    },

    /** @returns {string|null} URL Google Maps */
    mapsUrl() {
      if (!this.institutionAddress) return null;
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        this.institutionAddress
      )}`;
    },

    /** @returns {string} Descripción del médico */
    doctorDescription() {
      return this.doctor?.doctor?.description || "";
    },

    /**
     * @description Bio truncada o completa según bioExpanded
     * @returns {string}
     */
    doctorBio() {
      const desc = this.doctorDescription;
      if (!desc) return "";
      if (this.bioExpanded || desc.length <= 220) return desc;
      return desc.slice(0, 220) + "…";
    },

    /**
     * @description URL de foto de perfil del médico (avatar.key → S3)
     * @returns {string|null}
     */
    doctorPhoto() {
      return getPublicFileUrl(this.doctor?.avatar?.key);
    },

    /**
     * @description Estilo inline para el cover
     * @returns {Object}
     */
    coverStyle() {
      const url = getPublicFileUrl(this.doctor?.doctor?.coverPhoto?.key);
      if (url) {
        return {
          backgroundImage: `url(${url})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        };
      }
      return {};
    },

    /**
     * @description Obras sociales del médico (si el backend las expone)
     * @returns {Array}
     */
    doctorInsurances() {
      return (
        this.doctor?.doctor?.medicalInsurances ||
        this.doctor?.doctor?.insurances ||
        []
      );
    },

    /**
     * @description Si el médico ofrece teleconsulta
     * @returns {boolean}
     */
    doctorHasVirtual() {
      return this.doctor?.doctor?.hasVirtualAttention !== false;
    },

    /**
     * @description Ciudad resumida para el hero (ej: "Palermo, CABA")
     * @returns {string}
     */
    doctorCity() {
      const addr = this.doctorInstitution?.address;
      if (!addr) return "";
      if (typeof addr === "string") return addr;
      return [addr.neighborhood || addr.city, addr.state]
        .filter(Boolean)
        .join(", ");
    },

    /** @returns {boolean} Si la grilla ya está en el día de hoy (no retroceder más) */
    isAtToday() {
      return this.gridStartDay <= new Date();
    },

    /**
     * @description Días del mes actual (incluyendo padding de semanas completas, lun-dom).
     * @returns {Array<Date>}
     */
    monthDays() {
      const start = startOfWeek(startOfMonth(this.calendarMonth), {
        weekStartsOn: 1,
      });
      const end = endOfWeek(endOfMonth(this.calendarMonth), {
        weekStartsOn: 1,
      });
      return eachDayOfInterval({ start, end });
    },

    /**
     * @description Mapa date→{available, total} para el mes (para pintar el mini-cal)
     * @returns {Object}
     */
    monthAvailability() {
      const map = {};
      this.slots.forEach((s) => {
        const k = format(parseISO(s.startsAt), "yyyy-MM-dd");
        if (!map[k]) map[k] = { available: 0, total: 0 };
        map[k].total++;
        if (s.available) map[k].available++;
      });
      return map;
    },
  },

  async created() {
    await this.loadDoctorData();
    if (this.storeUser) {
      this.loggedPatient = this.storeUser;
    }
  },

  methods: {
    format,
    parseISO,
    isSameDay,
    addMonths,

    /**
     * @description Carga perfil del médico y slots disponibles
     */
    async loadDoctorData() {
      this.isLoading = true;
      try {
        const doctor = await turneraService.getDoctorProfile(this.doctorSlug);
        this.doctor = doctor;
        const slotsRes = await turneraService.getSlotsByDoctor(doctor._id);
        this.slots = slotsRes?.docs || slotsRes || [];
        const first = this.slots
          .filter((s) => s.available && new Date(s.startsAt) >= new Date())
          .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))[0];
        if (first) this.gridStartDay = parseISO(first.startsAt);
      } catch (err) {
        console.error("Error cargando turnera del médico:", err);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Selecciona un slot disponible y avanza al paso siguiente
     * @param {Object} slot
     */
    selectSlot(slot) {
      if (!slot.available) return;
      this.selectedSlot = slot;
      if (this.loggedPatient) {
        this.step = "confirmacion";
      } else {
        this.step = "identificacion";
      }
    },

    /**
     * @description Navega semanas en el week strip y la grilla
     * @param {number} dir - 1 adelante, -1 atrás (en semanas)
     */
    navigateGrid(dir) {
      this.gridStartDay = addDays(this.gridStartDay, dir * 7);
    },

    /**
     * @description Selecciona un día del week strip como inicio de la grilla
     * @param {Date} day
     */
    selectWeekDay(day) {
      this.gridStartDay = day;
    },

    /**
     * @description Cantidad de slots disponibles en un día (para el week strip)
     * @param {Date} day
     * @returns {number}
     */
    availableCountForDay(day) {
      return this.slots.filter(
        (s) => isSameDay(parseISO(s.startsAt), day) && s.available
      ).length;
    },

    /**
     * @description Si un día del week strip está en la ventana actual del grid
     * @param {Date} day
     * @returns {boolean}
     */
    isDayInGrid(day) {
      return this.gridDays.some((d) => isSameDay(d, day));
    },

    /**
     * @description Formatea la hora de un slot
     * @param {Object} slot
     * @returns {string}
     */
    formatSlotTime(slot) {
      if (!slot?.startsAt) return "";
      return format(parseISO(slot.startsAt), "HH:mm");
    },

    /**
     * @description Formatea encabezado de día para la grilla
     * @param {Date} date
     * @returns {{ label: string, day: string }}
     */
    formatGridDay(date) {
      const isToday = isSameDay(date, new Date());
      return {
        label: isToday
          ? "HOY"
          : format(date, "EEE.", { locale: es }).toUpperCase(),
        day: format(date, "d MMM", { locale: es }),
      };
    },

    /**
     * @description Wrapper de date-fns format para el template
     */
    formatDate(date, fmt) {
      return format(date, fmt, { locale: es });
    },

    /**
     * @description Login inline
     */
    async doLogin() {
      if (!this.loginForm.username || !this.loginForm.password) {
        this.$message.warning("Completá usuario y contraseña");
        return;
      }
      this.isLoggingIn = true;
      try {
        const res = await api.post("/auth/token", {
          username: this.loginForm.username,
          password: this.loginForm.password,
        });
        const { token, user } = res.data;
        this.$store.commit("setToken", token);
        this.$store.commit("setUser", user);
        this.loggedPatient = user;
        this.step = "confirmacion";
      } catch {
        this.$message.error("Usuario o contraseña incorrectos");
      } finally {
        this.isLoggingIn = false;
      }
    },

    /**
     * @description Registro inline
     */
    async doRegister() {
      const { firstName, lastName, email, governmentId } = this.registerForm;
      if (!firstName || !lastName || !email) {
        this.$message.warning("Completá nombre, apellido y email");
        return;
      }
      this.isRegistering = true;
      try {
        const res = await api.post("/landing/patient", {
          firstName,
          lastName,
          email,
          governmentId,
        });
        const { token, user } = res.data;
        this.$store.commit("setToken", token);
        this.$store.commit("setUser", user);
        this.loggedPatient = user;
        this.step = "confirmacion";
      } catch (err) {
        this.$message.error(
          err?.response?.data?.message || "Error al registrar el paciente"
        );
      } finally {
        this.isRegistering = false;
      }
    },

    /**
     * @description Confirma el turno
     */
    async confirmAppointment() {
      if (!this.selectedSlot || !this.loggedPatient) return;
      this.isConfirming = true;
      try {
        const payload = {
          slot: this.selectedSlot._id,
          patient: this.loggedPatient._id,
          attentionType:
            this.confirmForm.modalidad === "virtual" ? "virtual" : "in-person",
          reason: this.confirmForm.motivo,
          institution: this.selectedSlot.institution,
          medicalSpecialty: this.selectedSlot.medicalSpecialties?.[0],
        };
        const res = await api.post("/appointments", payload);
        this.confirmedAppointment = res.data;
        this.step = "exito";
      } catch {
        this.$message.error("Error al confirmar el turno");
      } finally {
        this.isConfirming = false;
      }
    },

    /**
     * @description Navega al portal del paciente
     */
    goToPortal() {
      this.$router.push({ name: "portal" });
    },

    /**
     * @description Navega meses en la vista de mes
     * @param {number} dir - 1 adelante, -1 atrás
     */
    navigateMonth(dir) {
      this.calendarMonth = addMonths(this.calendarMonth, dir);
    },

    /**
     * @description Selecciona un día del mini-cal de mes y vuelve a vista semana
     * @param {Date} day
     */
    selectMonthDay(day) {
      this.gridStartDay = day;
      this.calendarMode = "week";
    },

    /**
     * @description Desloguea al paciente de la turnera
     */
    logout() {
      this.$store.commit("setUser", null);
      this.$store.commit("setToken", null);
      this.loggedPatient = null;
    },
  },
};
</script>

<template lang="pug">
.doctor-booking
  spinner(v-if="isLoading")

  template(v-else)
    //- ── COVER estilo LinkedIn ──────────────────────────────
    .db-cover(:style="coverStyle")
      .db-cover__overlay
      .db-cover__portal-btn
        router-link.db-portal-top-btn(:to="{ name: 'portal' }")
          span.material-icons-outlined person
          | Portal paciente
      .db-cover__inner
        .db-cover__photo
          img(v-if="doctorPhoto" :src="doctorPhoto" :alt="doctorFullName")
          span.material-icons-outlined(v-else) person
        .db-cover__info
          p.db-cover__supra Reservá tu turno online
          h1.db-cover__name {{ doctorFullName }}
          .db-cover__pills(v-if="doctorSpecialties.length")
            span.db-cover__pill(v-for="s in doctorSpecialties" :key="s._id") {{ s.name }}
          .db-cover__meta
            span.db-cover__meta-item(v-if="doctorLicense")
              span.material-icons-outlined badge
              | {{ doctorLicense }}
            span.db-cover__meta-item(v-if="doctorCity")
              span.material-icons-outlined place
              | {{ doctorCity }}
            span.db-cover__meta-item(v-if="doctorHasVirtual")
              span.material-icons-outlined videocam
              | Atención virtual disponible

    //- ── BODY 2 columnas ────────────────────────────────────
    .db-layout

      //- Columna izquierda: perfil
      .db-profile-col

        //- Consultorio
        .db-section-card(v-if="institutionAddress || institutionPhone || institutionEmail")
          .db-section-card__title Consultorio
          .db-info-rows
            .db-info-row(v-if="institutionAddress")
              span.material-icons-outlined place
              .db-address-block
                span.db-address-text {{ institutionAddress }}
                button.db-map-toggle(@click="mapExpanded = !mapExpanded")
                  span.material-icons-outlined {{ mapExpanded ? 'expand_less' : 'map' }}
                  | {{ mapExpanded ? 'Cerrar mapa' : 'Ver mapa' }}
                .db-map-embed(v-if="mapExpanded && mapsUrl")
                  iframe(
                    :src="`https://maps.google.com/maps?q=${encodeURIComponent(institutionAddress)}&output=embed`"
                    width="100%"
                    height="200"
                    frameborder="0"
                    style="border-radius:8px; margin-top:8px;"
                    allowfullscreen
                  )
            .db-info-row(v-if="institutionPhone")
              span.material-icons-outlined phone
              a(:href="`tel:${institutionPhone}`") {{ institutionPhone }}
            .db-info-row(v-if="institutionEmail")
              span.material-icons-outlined mail
              a(:href="`mailto:${institutionEmail}`") {{ institutionEmail }}

        //- Sobre mí
        .db-section-card(v-if="doctorDescription")
          .db-section-card__title Sobre mí
          p.db-bio-text {{ doctorBio }}
          span.db-bio-more(
            v-if="doctorDescription.length > 220"
            @click="bioExpanded = !bioExpanded"
          ) {{ bioExpanded ? 'ver menos ‹' : 'ver más ›' }}

        //- Especialidades
        .db-section-card(v-if="doctorSpecialties.length")
          .db-section-card__title Especialidades
          .db-spec-list
            span.db-spec-tag(v-for="s in doctorSpecialties" :key="s._id") {{ s.name }}

        //- Formatos de atención
        .db-section-card
          .db-section-card__title Formatos de atención
          .db-att-badges
            .db-att-badge
              span.material-icons-outlined person
              | Presencial
            .db-att-badge(v-if="doctorHasVirtual")
              span.material-icons-outlined videocam
              | Teleconsulta

      //- Columna derecha: booking
      .db-booking-col

        //- Banner portal — siempre visible
        .db-portal-banner
          template(v-if="loggedPatient")
            span.material-icons-outlined account_circle
            span.db-portal-banner__name Hola, {{ loggedPatient.firstName }}
            a.db-portal-link(@click="goToPortal") Ir a mi portal
            button.db-portal-logout(@click="logout")
              span.material-icons-outlined logout
          template(v-else)
            span.material-icons-outlined account_circle
            span.db-portal-banner__name Login portal paciente
            router-link.db-portal-link(:to="{ name: 'portal' }") Ingresar

        //- ── PASO: TURNOS ──
        .db-booking-card(v-if="step === 'turnos'")
          .db-booking-card__header
            p.db-booking-card__header-label Próximos turnos disponibles
            p.db-booking-card__header-name {{ doctorFullName }}
          .db-booking-card__body
            .db-bk-field
              label.db-bk-label Tipo de consulta
              select.db-bk-select(v-model="selectedService")
                option(value="") Consulta clínica general
                option(v-for="s in doctorSpecialties" :key="s._id" :value="s._id") {{ s.name }}
            .db-bk-field(v-if="doctorInsurances.length")
              label.db-bk-label Obra social / prepaga
              select.db-bk-select(v-model="selectedInsurance")
                option(value="") Sin cobertura (particular)
                option(v-for="ins in doctorInsurances" :key="ins._id" :value="ins._id") {{ ins.name }}
            .db-bk-field
              label.db-bk-label ¿Es tu primera consulta?
              .db-bk-toggle
                button.db-bk-toggle-btn(
                  :class="{ 'db-bk-toggle-btn--active': isFirstVisit }"
                  @click="isFirstVisit = true"
                ) Sí, primera vez
                button.db-bk-toggle-btn(
                  :class="{ 'db-bk-toggle-btn--active': !isFirstVisit }"
                  @click="isFirstVisit = false"
                ) Ya soy paciente
            .db-bk-divider
            //- Toggle semana / mes
            .db-cal-tabs
              button.db-cal-tab(
                :class="{ 'db-cal-tab--active': calendarMode === 'week' }"
                @click="calendarMode = 'week'"
              ) Semana
              button.db-cal-tab(
                :class="{ 'db-cal-tab--active': calendarMode === 'month' }"
                @click="calendarMode = 'month'"
              ) Mes

            //- Vista MES
            .db-month-cal(v-if="calendarMode === 'month'")
              .db-month-cal__header
                button.db-week-nav(@click="navigateMonth(-1)")
                  span.material-icons-outlined chevron_left
                span.db-month-cal__title {{ format(calendarMonth, 'MMMM yyyy', { locale: es }) }}
                button.db-week-nav(@click="navigateMonth(1)")
                  span.material-icons-outlined chevron_right
              .db-month-cal__days-header
                span(v-for="d in ['Lu','Ma','Mi','Ju','Vi','Sa','Do']" :key="d") {{ d }}
              .db-month-cal__grid
                button.db-month-day(
                  v-for="(day, di) in monthDays"
                  :key="di"
                  :class="{ \
                    'db-month-day--other': day.getMonth() !== calendarMonth.getMonth(), \
                    'db-month-day--has-slots': monthAvailability[format(day, 'yyyy-MM-dd')] && monthAvailability[format(day, 'yyyy-MM-dd')].available > 0, \
                    'db-month-day--active': isSameDay(day, gridStartDay) \
                  }"
                  @click="selectMonthDay(day)"
                )
                  span {{ format(day, 'd') }}
                  span.db-month-day__dot(v-if="monthAvailability[format(day, 'yyyy-MM-dd')] && monthAvailability[format(day, 'yyyy-MM-dd')].available > 0")

            //- Vista SEMANA — week strip: 7 días navegables
            .db-week-strip(v-else)
              button.db-week-nav(
                @click="navigateGrid(-1)"
                :disabled="isAtToday"
              )
                span.material-icons-outlined chevron_left
              .db-week-days
                button.db-week-day(
                  v-for="(day, i) in weekStripDays"
                  :key="i"
                  :class="{ \
                    'db-week-day--active': isDayInGrid(day), \
                    'db-week-day--empty': !availableCountForDay(day) \
                  }"
                  @click="selectWeekDay(day)"
                )
                  span.db-week-day__label {{ format(day, 'EEE', { locale: es }).slice(0,2).toUpperCase() }}
                  span.db-week-day__num {{ format(day, 'd') }}
                  span.db-week-day__dot(v-if="availableCountForDay(day)")
              button.db-week-nav(@click="navigateGrid(1)")
                span.material-icons-outlined chevron_right
            //- Grid de 3 días
            .db-grid
              .db-grid__col(v-for="(day, di) in gridDays" :key="di")
                .db-grid__day-header
                  span.db-grid__day-label {{ formatGridDay(day).label }}
                  span.db-grid__day-date {{ formatGridDay(day).day }}
                template(v-if="slotsByDay[format(day, 'yyyy-MM-dd')].length")
                  button.db-slot(
                    v-for="slot in slotsByDay[format(day, 'yyyy-MM-dd')]"
                    :key="slot._id"
                    :class="{ 'db-slot--taken': !slot.available }"
                    :disabled="!slot.available"
                    @click="selectSlot(slot)"
                  ) {{ formatSlotTime(slot) }}
                .db-no-slots(v-else) —
            .db-empty(v-if="!hasSlots")
              span.material-icons-outlined event_busy
              p Sin turnos disponibles en este período
          .db-booking-card__footer
            span.material-icons-outlined lock
            | Confirmación instantánea · sin cargo

        //- ── PASO: IDENTIFICACIÓN ──
        .db-booking-card(v-if="step === 'identificacion'")
          button.db-btn-back(@click="step = 'turnos'")
            span.material-icons-outlined arrow_back
            | Volver
          h2.db-booking-card__step-title Identificate para confirmar
          .db-auth-tabs
            button.db-auth-tab(
              :class="{ active: authTab === 'login' }"
              @click="authTab = 'login'"
            ) Ya tengo cuenta
            button.db-auth-tab(
              :class="{ active: authTab === 'register' }"
              @click="authTab = 'register'"
            ) Soy nuevo
          .db-auth-panel(v-if="authTab === 'login'")
            .db-field
              label.db-label Usuario o DNI
              el-input(v-model="loginForm.username" placeholder="usuario o DNI" size="small")
            .db-field
              label.db-label Contraseña
              el-input(
                v-model="loginForm.password"
                type="password"
                placeholder="contraseña"
                size="small"
                @keyup.enter.native="doLogin"
              )
            button.db-btn-primary.wide(
              @click="doLogin"
              :disabled="isLoggingIn"
            ) {{ isLoggingIn ? 'Ingresando...' : 'Ingresar y confirmar' }}
            p.db-auth-hint
              | ¿Olvidaste tu contraseña?&nbsp;
              router-link(:to="{ name: 'password-forgot' }") Recuperala acá
          .db-auth-panel(v-if="authTab === 'register'")
            .db-field-row
              .db-field
                label.db-label Nombre
                el-input(v-model="registerForm.firstName" placeholder="nombre" size="small")
              .db-field
                label.db-label Apellido
                el-input(v-model="registerForm.lastName" placeholder="apellido" size="small")
            .db-field
              label.db-label Email
              el-input(v-model="registerForm.email" type="email" placeholder="email@ejemplo.com" size="small")
            .db-field
              label.db-label DNI (opcional)
              el-input(v-model="registerForm.governmentId.number" placeholder="sin puntos" size="small")
            button.db-btn-primary.wide(
              @click="doRegister"
              :disabled="isRegistering"
            ) {{ isRegistering ? 'Registrando...' : 'Registrarme y confirmar' }}

        //- ── PASO: CONFIRMACIÓN ──
        .db-booking-card(v-if="step === 'confirmacion'")
          button.db-btn-back(@click="step = loggedPatient ? 'turnos' : 'identificacion'")
            span.material-icons-outlined arrow_back
            | Volver
          h2.db-booking-card__step-title Confirmá tu turno
          .db-summary
            .db-summary__row
              span.material-icons-outlined event
              span(v-if="selectedSlot")
                | {{ formatDate(parseISO(selectedSlot.startsAt), "d 'de' MMMM 'a las' HH:mm") }}
            .db-summary__row(v-if="institutionAddress")
              span.material-icons-outlined place
              span {{ institutionAddress }}
            .db-summary__row(v-if="loggedPatient")
              span.material-icons-outlined person_outline
              span {{ loggedPatient.firstName }} {{ loggedPatient.lastName }}
          .db-field
            label.db-label Modalidad
            .db-radio-group
              label.db-radio
                input(type="radio" v-model="confirmForm.modalidad" value="presencial")
                span Presencial
              label.db-radio(v-if="doctorHasVirtual")
                input(type="radio" v-model="confirmForm.modalidad" value="virtual")
                span Virtual
          .db-field
            label.db-label Motivo (opcional)
            el-input(
              v-model="confirmForm.motivo"
              type="textarea"
              :rows="3"
              placeholder="Describí brevemente el motivo..."
            )
          button.db-btn-primary.wide(
            @click="confirmAppointment"
            :disabled="isConfirming"
          ) {{ isConfirming ? 'Confirmando...' : 'Confirmar turno' }}

        //- ── PASO: ÉXITO ──
        .db-booking-card.db-booking-card--success(v-if="step === 'exito'")
          .db-success__icon
            span.material-icons-outlined check_circle
          h2.db-success__title ¡Turno confirmado!
          p.db-success__sub Tu turno fue agendado correctamente.
          .db-summary(v-if="selectedSlot")
            .db-summary__row
              span.material-icons-outlined person
              span {{ doctorFullName }}
            .db-summary__row
              span.material-icons-outlined event
              span {{ formatDate(parseISO(selectedSlot.startsAt), "d 'de' MMMM 'a las' HH:mm") }}
            .db-summary__row(v-if="institutionAddress")
              span.material-icons-outlined place
              span {{ institutionAddress }}
          .db-success__actions
            button.db-btn-primary(@click="goToPortal") Acceder a mi portal
            button.db-btn-secondary(@click="step = 'turnos'") Sacar otro turno
</template>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/icon?family=Material+Icons+Outlined");

$navy: #1e3a5f;
$navy-mid: #2563a8;
$teal: #0d9d9d;
$teal-light: rgba(13, 157, 157, 0.09);
$teal-mid: rgba(13, 157, 157, 0.18);
$gray-50: #f6f8fb;
$gray-100: #edf1f7;
$gray-200: #d9e2ef;
$gray-300: #afc0d4;
$gray-400: #7a96b0;
$gray-500: #4a6070;
$white: #ffffff;
$success: #16a34a;

.doctor-booking {
  min-height: 100vh;
  background: $gray-50;
}

/* ── Cover LinkedIn ─────────────────────────────── */
.db-cover {
  position: relative;
  height: 240px;
  overflow: visible;
  background: linear-gradient(135deg, #1a4480 0%, #1e6fa8 45%, #0d7a9d 100%);
  background-size: cover;
  background-position: center;

  &__overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      transparent 35%,
      rgba(15, 35, 70, 0.65) 100%
    );
    z-index: 1;
  }

  &__inner {
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 32px 20px;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: flex-end;
    gap: 20px;
    z-index: 2;
  }

  &__photo {
    width: 128px;
    height: 128px;
    min-width: 128px;
    border-radius: 14px;
    background: linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.25),
      rgba(255, 255, 255, 0.1)
    );
    border: 3px solid $white;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: flex-end;
    flex-shrink: 0;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .material-icons-outlined {
      font-size: 60px;
      color: rgba(255, 255, 255, 0.88);
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }
  }

  &__info {
    flex: 1;
    padding-bottom: 18px;
  }

  &__supra {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.5);
    margin: 0 0 5px;
  }

  &__name {
    font-size: 26px;
    font-weight: 800;
    color: $white;
    line-height: 1.15;
    margin: 0 0 9px;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    /* BUG-NU9AGU: DB guarda firstName/lastName en lowercase. doctorFullName
       arma "Dr. ${lastName}, ${firstName}" sin capitalize. Defensive CSS. */
    text-transform: capitalize;
  }

  &__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 10px;
  }

  &__pill {
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.28);
    border-radius: 20px;
    padding: 3px 11px;
    font-size: 11px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
  }

  &__meta {
    display: flex;
    gap: 18px;
    flex-wrap: wrap;
  }

  &__meta-item {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);

    .material-icons-outlined {
      font-size: 14px;
      opacity: 0.8;
    }
  }
}

/* ── Layout 2 columnas ──────────────────────────── */
.db-layout {
  max-width: 1100px;
  margin: 0 auto;
  padding: 28px 32px 60px;
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 28px;
  align-items: start;
}

/* ── Columna izquierda ──────────────────────────── */
.db-profile-col {
}

.db-section-card {
  background: $white;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  padding: 24px;
  margin-bottom: 20px;

  &__title {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: $navy;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid $gray-100;
  }
}

.db-info-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.db-info-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: $gray-500;

  .material-icons-outlined {
    font-size: 17px;
    color: $teal;
    flex-shrink: 0;
  }

  a {
    color: $teal;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.db-bio-text {
  font-size: 14px;
  line-height: 1.7;
  color: $gray-500;
  margin: 0;
}

.db-bio-more {
  color: $teal;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 6px;
  display: inline-block;
}

.db-spec-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.db-spec-tag {
  background: $teal-light;
  border: 1px solid $teal-mid;
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  color: $teal;
}

.db-att-badges {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.db-att-badge {
  display: flex;
  align-items: center;
  gap: 7px;
  background: $gray-50;
  border: 1px solid $gray-200;
  border-radius: 10px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  color: $navy;

  .material-icons-outlined {
    font-size: 17px;
    color: $teal;
  }
}

/* ── Columna derecha — booking sticky ──────────── */
.db-booking-col {
  position: sticky;
  top: 20px;
}

.db-booking-card {
  background: $white;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  &__header {
    background: linear-gradient(135deg, $navy, $navy-mid);
    padding: 16px 20px;
  }

  &__header-label {
    font-size: 11px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin: 0 0 2px;
  }

  &__header-name {
    font-size: 15px;
    font-weight: 700;
    color: $white;
    margin: 0;
    /* BUG-NU9AGU: idem .db-cover__name */
    text-transform: capitalize;
  }

  &__step-title {
    font-size: 16px;
    font-weight: 700;
    color: $navy;
    margin: 0 0 18px;
  }

  &__body {
    padding: 18px 20px;
  }

  &__footer {
    background: $gray-50;
    border-top: 1px solid $gray-100;
    padding: 11px 20px;
    font-size: 11px;
    color: $gray-300;
    display: flex;
    align-items: center;
    gap: 6px;

    .material-icons-outlined {
      font-size: 13px;
    }
  }

  &--success {
    padding: 28px 24px;
    text-align: center;
  }
}

/* Padding de body del card (sin header) */
.db-booking-card > .db-btn-back,
.db-booking-card > .db-booking-card__step-title,
.db-booking-card > .db-auth-tabs,
.db-booking-card > .db-auth-panel,
.db-booking-card > .db-summary,
.db-booking-card > .db-field,
.db-booking-card > .db-btn-primary,
.db-booking-card > .db-btn-secondary {
  margin-left: 20px;
  margin-right: 20px;

  &:first-child {
    margin-top: 18px;
  }
  &:last-child {
    margin-bottom: 18px;
  }
}

/* ── Booking fields ─────────────────────────────── */
.db-bk-field {
  margin-bottom: 13px;
}

.db-bk-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: $gray-400;
  margin-bottom: 5px;
}

.db-bk-select {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid $gray-200;
  border-radius: 9px;
  font-size: 13px;
  font-family: inherit;
  color: $navy;
  background: $white;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%237a96b0' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 20px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: $teal;
  }
}

.db-bk-toggle {
  display: flex;
  border: 1.5px solid $gray-200;
  border-radius: 9px;
  overflow: hidden;
}

.db-bk-toggle-btn {
  flex: 1;
  padding: 8px 4px;
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 600;
  color: $gray-400;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;

  &--active {
    background: $navy;
    color: $white;
  }

  & + & {
    border-left: 1.5px solid $gray-200;
  }
}

.db-bk-divider {
  height: 1px;
  background: $gray-100;
  margin: 14px 0;
}

/* ── Portal banner ──────────────────────────────── */
/* ── Portal top button (cover) ──────────────────── */
.db-cover__portal-btn {
  position: absolute;
  top: 14px;
  right: 18px;
  z-index: 3;
}

.db-portal-top-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.18);
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(6px);
  border-radius: 20px;
  padding: 5px 13px 5px 8px;
  font-size: 12px;
  font-weight: 700;
  color: $white;
  text-decoration: none;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.32);
    color: $white;
  }

  .material-icons-outlined {
    font-size: 16px;
  }
}

/* ── Portal banner (booking col) ───────────────── */
.db-portal-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  background: $teal-light;
  border: 1px solid $teal-mid;
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 10px;
  font-size: 13px;
  color: $navy;

  .material-icons-outlined {
    font-size: 18px;
    color: $teal;
    flex-shrink: 0;
  }

  &__name {
    flex: 1;
    font-weight: 600;
    /* BUG-NU9AGU: "Hola, federico" — el firstName del paciente raw.
       Capitalize defensivo. */
    text-transform: capitalize;
  }
}

.db-portal-link {
  font-size: 12px;
  font-weight: 700;
  color: $teal;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}

.db-portal-logout {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: $gray-400;
  display: flex;
  align-items: center;
  border-radius: 4px;
  transition: color 0.15s;
  &:hover {
    color: #e53e3e;
  }
  .material-icons-outlined {
    font-size: 16px;
  }
}

/* ── Cal tabs (semana/mes) ──────────────────────── */
.db-cal-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 10px;
}

.db-cal-tab {
  flex: 1;
  padding: 5px;
  font-size: 11px;
  font-weight: 700;
  border: 1.5px solid $gray-200;
  border-radius: 7px;
  background: none;
  color: $gray-400;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.3px;

  &--active {
    background: $navy;
    border-color: $navy;
    color: $white;
  }

  &:hover:not(.db-cal-tab--active) {
    border-color: $gray-300;
    color: $navy;
  }
}

/* ── Month calendar ─────────────────────────────── */
.db-month-cal {
  margin-bottom: 12px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    text-transform: capitalize;
  }

  &__days-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-bottom: 4px;

    span {
      text-align: center;
      font-size: 9px;
      font-weight: 700;
      color: $gray-300;
      letter-spacing: 0.3px;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
  }
}

.db-month-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: none;
  cursor: pointer;
  transition: all 0.15s;
  position: relative;

  span:first-child {
    font-size: 11px;
    font-weight: 600;
    color: $navy;
    line-height: 1;
  }

  &__dot {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: $teal;
    position: absolute;
    bottom: 3px;
  }

  &:hover {
    background: $gray-100;
  }

  &--other span:first-child {
    color: $gray-300;
  }

  &--has-slots {
    cursor: pointer;
  }

  &--active {
    background: $teal !important;
    span:first-child {
      color: $white;
    }
    .db-month-day__dot {
      background: $white;
    }
  }
}

/* ── Address + map ──────────────────────────────── */
.db-address-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.db-address-text {
  font-size: 14px;
  color: $navy;
}

.db-map-toggle {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: none;
  border: 1px solid $gray-200;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  color: $teal;
  cursor: pointer;
  align-self: flex-start;
  transition: all 0.15s;

  &:hover {
    border-color: $teal;
  }
  .material-icons-outlined {
    font-size: 13px;
  }
}

/* ── Week strip ─────────────────────────────────── */
.db-week-strip {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 12px;
}

.db-week-nav {
  background: none;
  border: 1.5px solid $gray-200;
  border-radius: 7px;
  width: 24px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  color: $gray-400;
  transition: all 0.15s;
  padding: 0;

  &:hover:not(:disabled) {
    border-color: $teal;
    color: $teal;
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  .material-icons-outlined {
    font-size: 15px;
  }
}

.db-week-days {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.db-week-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 5px 0;
  border-radius: 7px;
  border: 1.5px solid transparent;
  cursor: pointer;
  background: none;
  transition: all 0.15s;

  &__label {
    font-size: 8px;
    font-weight: 700;
    color: $gray-300;
    letter-spacing: 0.3px;
    line-height: 1;
  }

  &__num {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    line-height: 1;
  }

  &__dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: $teal;
    display: block;
  }

  &:hover:not(.db-week-day--active) {
    background: $gray-100;
  }

  &--active {
    background: $teal-light;
    border-color: $teal;
    .db-week-day__label {
      color: $teal;
    }
    .db-week-day__num {
      color: $teal;
    }
  }

  &--empty {
    .db-week-day__num {
      color: $gray-300;
    }
  }
}

/* ── Grid de slots ──────────────────────────────── */
.db-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;

  &__day-header {
    text-align: center;
    padding: 0 2px 8px;
    border-bottom: 1.5px solid $gray-100;
    margin-bottom: 6px;
  }

  &__day-label {
    display: block;
    font-size: 9px;
    font-weight: 700;
    color: $gray-300;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  &__day-date {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: $navy;
    margin-top: 2px;
  }
}

.db-slot {
  display: block;
  width: 100%;
  text-align: center;
  padding: 7px 2px;
  margin-bottom: 5px;
  background: $teal-light;
  color: $teal;
  border: 1.5px solid transparent;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: $teal;
    color: $white;
    border-color: $teal;
  }

  &--taken {
    background: $gray-100;
    color: $gray-300;
    border: 1.5px solid $gray-100;
    cursor: not-allowed;

    &:hover {
      background: $gray-100;
      color: $gray-300;
      border-color: $gray-100;
    }
  }
}

.db-no-slots {
  text-align: center;
  color: $gray-200;
  font-size: 16px;
  padding: 8px 0;
}

.db-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 0 8px;
  color: $gray-400;

  .material-icons-outlined {
    font-size: 32px;
  }

  p {
    font-size: 13px;
    margin: 0;
  }
}

/* ── Auth ───────────────────────────────────────── */
.db-auth-tabs {
  display: flex;
  border: 1.5px solid $gray-200;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
}

.db-auth-tab {
  flex: 1;
  padding: 9px;
  font-size: 12px;
  font-weight: 600;
  background: $white;
  border: none;
  cursor: pointer;
  color: $gray-400;
  transition: all 0.15s;
  font-family: inherit;

  &.active {
    background: $navy;
    color: $white;
  }
}

.db-auth-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.db-auth-hint {
  font-size: 12px;
  color: $gray-400;
  text-align: center;
  margin: 0;

  a {
    color: $teal;
  }
}

/* ── Fields ─────────────────────────────────────── */
.db-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 12px;
}

.db-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.db-label {
  font-size: 11px;
  font-weight: 700;
  color: $gray-500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* ── Resumen ─────────────────────────────────────── */
.db-summary {
  background: $gray-50;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: $gray-500;

    .material-icons-outlined {
      font-size: 15px;
      color: $teal;
      flex-shrink: 0;
    }
  }
}

/* ── Radio group ─────────────────────────────────── */
.db-radio-group {
  display: flex;
  gap: 16px;
}

.db-radio {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: $gray-500;
  cursor: pointer;
}

/* ── Éxito ───────────────────────────────────────── */
.db-success {
  &__icon .material-icons-outlined {
    font-size: 52px;
    color: $success;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: $navy;
    margin: 10px 0 6px;
  }

  &__sub {
    color: $gray-400;
    font-size: 13px;
    margin: 0 0 16px;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
    margin-top: 16px;
  }
}

/* ── Botones ─────────────────────────────────────── */
.db-btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: $gray-400;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-bottom: 14px;
  transition: color 0.15s;
  font-family: inherit;

  &:hover {
    color: $navy;
  }

  .material-icons-outlined {
    font-size: 15px;
  }
}

.db-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 11px 24px;
  background: $teal;
  color: $white;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;

  &:hover:not(:disabled) {
    background: darken($teal, 8%);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &.wide {
    width: 100%;
    margin-top: 4px;
  }
}

.db-btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 11px 24px;
  background: $white;
  color: $navy;
  border: 1.5px solid $gray-200;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
  text-decoration: none;

  &:hover {
    border-color: $navy;
  }
}

/* ── Responsivo ──────────────────────────────────── */
@media (max-width: 860px) {
  .db-layout {
    grid-template-columns: 1fr;
    padding: 20px 16px 40px;
  }

  .db-booking-col {
    position: static;
    order: -1;
  }

  .db-cover {
    height: 200px;

    &__photo {
      width: 96px;
      height: 96px;
      min-width: 96px;
    }

    &__name {
      font-size: 20px;
    }
  }

  .db-cover__inner {
    padding: 0 16px 16px;
  }
}
</style>
